import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import {
  ISearchQuery,
  ISearchQueryVariables,
  Maybe,
} from '../../../utils/apollo/graphqlSchema';
import { SEARCH_QUERY } from '../../../utils/apollo/universalSearch';
import SearchMenu from './SearchMenu';
import { useImmer } from 'use-immer';
import { defaultSearchInput, SEARCH_COUNT } from './utils';
import { ISearchInput, UniversalSearchMenuControls } from './types';
import { useDebounce } from '../../../utils/hooks';
import { getAdvancedSearchConfig } from './SearchResultItem/utils';
import SearchBar from '../SearchBar';
import {
  autoUpdate,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { useCommandK } from './useCommandK';
import { HEADER_Z_INDEX } from '../../../styles/zIndexes';
import FloatingLayer from '../../../components/FloatingManager/FloatingLayer';
import { theme } from '../../../styles/theme';

interface IProps {
  onToggleFavoritesOff?: () => void;
}

const OutsideClickWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${HEADER_Z_INDEX - 1};
`;

const SearchWrapper = styled.div`
  position: relative;
  z-index: ${HEADER_Z_INDEX};
  width: 460px;
`;

const FloatingWrapper = styled.div`
  border-radius: 4px;
  background-color: ${theme.colors.level2};
  box-shadow: 0 4px 32px 0 rgba(15, 23, 42, 0.3);
`;

const UniversalSearch = forwardRef<UniversalSearchMenuControls, IProps>(
  ({ onToggleFavoritesOff }, ref) => {
    const [searchInput, setSearchInput] =
      useImmer<ISearchInput>(defaultSearchInput);
    const [searchResult, setSearchResult] =
      useState<Maybe<ISearchQuery['search']>>(null);
    const onFetchSearchResult = (data: ISearchQuery) => {
      if (data.search.pageInfo.first > 0 && searchResult) {
        setSearchResult({
          ...data.search,
          entities: [...searchResult.entities, ...data.search.entities],
          pageInfo: data.search.pageInfo,
        });
      } else {
        setSearchResult(data.search);
      }
    };

    const [isOpen, setIsOpen] = useState(false);

    const inputRef = useCommandK();

    const { refs, floatingStyles, context } = useFloating({
      open: isOpen,
      onOpenChange: (open) => {
        setIsOpen(open);
        if (!open) {
          if (searchInput.favoriteOnly) {
            // if favorites enabled and we close, clear the favoritesOnly state
            searchInputUpdateHandler(
              {
                ...searchInput,
                favoriteOnly: false,
              },
              false,
            );
          }

          onToggleFavoritesOff?.();
        }
      },
      middleware: [offset(5), shift()],
      whileElementsMounted: autoUpdate,
      placement: 'bottom',
    });
    const click = useClick(context, {
      toggle: false,
      keyboardHandlers: false,
    });
    const dismiss = useDismiss(context);
    const role = useRole(context);
    const headingId = useId();
    const { getReferenceProps, getFloatingProps } = useInteractions([
      click,
      dismiss,
      role,
    ]);

    const searchInputUpdateHandler = useCallback(
      (searchInput: ISearchInput, isLoadMore: boolean) => {
        if (isLoadMore) {
          setSearchInput((draft) => {
            draft.pagination = {
              after: searchResult ? searchResult.pageInfo.last + 1 : 0,
              first: SEARCH_COUNT,
            };
          });
        } else {
          // if not load more, reset pagination config
          setSearchInput({
            ...searchInput,
            pagination: { first: SEARCH_COUNT, after: 0 },
          });
        }
      },
      [setSearchInput, searchResult],
    );
    const debouncedSearchInput = useDebounce(searchInput, 300);
    // by reading searchConfig and decide what variable need to be dropped
    const getUpdatedVariables = useCallback((currSearchInput: ISearchInput) => {
      if (currSearchInput.types.length === 0) {
        return currSearchInput;
      } else {
        const advancedSearchConfig = getAdvancedSearchConfig(
          currSearchInput.types,
        );
        return {
          ...currSearchInput,
          editorIds: advancedSearchConfig.editorIds
            ? currSearchInput.editorIds
            : [],
          schemaIds: advancedSearchConfig.schemaIds
            ? currSearchInput.schemaIds
            : [],
          queryTypes: advancedSearchConfig.queryTypes
            ? currSearchInput.queryTypes
            : [],
          categoryIds: advancedSearchConfig.categoryIds
            ? currSearchInput.categoryIds
            : [],
        };
      }
    }, []);

    const { loading } = useQuery<ISearchQuery, ISearchQueryVariables>(
      SEARCH_QUERY,
      {
        variables: getUpdatedVariables(debouncedSearchInput),
        onCompleted: onFetchSearchResult,
        skip: !isOpen,
      },
    );

    const handleClose = () => {
      setIsOpen(false);
      onToggleFavoritesOff?.();
    };

    // Expose the `openMenuWithFavoritesOnly` method to parent components which allows the parent component
    // to programmatically open the menu and set the `favoriteOnly` state to true
    useImperativeHandle(
      ref,
      () => ({
        openMenuWithFavoritesOnly: () => {
          searchInputUpdateHandler(
            {
              ...searchInput,
              favoriteOnly: true,
            },
            false,
          );
          setIsOpen((prevState) => !prevState);
          if (inputRef.current) {
            inputRef.current.focus();
          }
        },
      }),
      [inputRef, searchInput, searchInputUpdateHandler],
    );

    return (
      <>
        {isOpen && <OutsideClickWrapper onClick={handleClose} />}
        <SearchWrapper ref={refs.setReference} {...getReferenceProps()}>
          <SearchBar
            ref={inputRef}
            value={searchInput.term}
            onChange={(term) =>
              searchInputUpdateHandler({ ...searchInput, term }, false)
            }
            placeholder={'Search ⌘ + K'}
          />
        </SearchWrapper>
        {isOpen && (
          <FloatingLayer>
            <FloatingWrapper
              ref={refs.setFloating}
              style={floatingStyles}
              aria-labelledby={headingId}
              {...getFloatingProps()}
            >
              <SearchMenu
                searchInput={searchInput}
                setSearchInput={searchInputUpdateHandler}
                searchResult={searchResult}
                // loading animate will cause the re-render on list, therefore no need to show loading on LOAD MORE
                loading={loading && searchResult?.pageInfo.first === 0}
                handleMenuClose={handleClose}
                isFavoriteOnly={searchInput.favoriteOnly}
                toggleFavoriteOnly={(favoriteOnly: boolean) => {
                  searchInputUpdateHandler(
                    {
                      ...searchInput,
                      favoriteOnly,
                    },
                    false,
                  );
                  onToggleFavoritesOff?.();
                }}
              />
            </FloatingWrapper>
          </FloatingLayer>
        )}
      </>
    );
  },
);

export default UniversalSearch;
